import React, { useState } from "react";

import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import CardFastAsset from "./CardFastAsset";
import CardOttAsset from "./CardOttAsset";
import Button from "../../button/Button";
import SidePanel from "../../sidePanel/SidePanel";

import type {
  EncodingStream,
  StreamType,
  UpdateStreamData,
} from "../../../api/EncoderAPI.types";
import type { FastAsset } from "../../../api/MediaAPI.types";
import type { AppState } from "../../../reducers";

type StreamFilter = "all" | StreamType | "webVtt";

const FILTERS: StreamFilter[] = [
  "all",
  "video",
  "audio",
  "fast",
  "d2g",
  "webVtt",
];

interface StreamItem {
  kind: "stream";
  data: EncodingStream;
  type: StreamType;
}
interface FastAssetItem {
  kind: "fast-asset";
  data: FastAsset;
  type: "fast";
}
type DisplayItem = StreamItem | FastAssetItem;

function itemMatchesFilter(item: DisplayItem, filter: StreamFilter): boolean {
  if (filter === "webVtt") {
    return item.type === "subtitles" || item.type === "chapters";
  }

  return filter === "all" || item.type === filter;
}

const GROUP_ORDER = ["vodDelivery", "offline", "linearDelivery"] as const;
type GroupName = (typeof GROUP_ORDER)[number];

function groupForItem(item: DisplayItem): GroupName {
  if (item.kind === "fast-asset") return "linearDelivery";
  if (item.type === "d2g") return "offline";
  return "vodDelivery";
}

interface EncodingOperationsDrawerProps {
  isOpen: boolean;
  streams: EncodingStream[];
  fastAsset: FastAsset | null;
  onClose: () => void;
  onDeleteStream: (streamId: string) => void;
  onUpdateStream: (streamId: string, data: UpdateStreamData) => void;
  onDeleteFastAsset: () => void;
}

export default function EncodingOperationsDrawer({
  isOpen,
  streams,
  fastAsset,
  onClose,
  onDeleteStream,
  onUpdateStream,
  onDeleteFastAsset,
}: EncodingOperationsDrawerProps): React.JSX.Element {
  const { t } = useTranslation("editor-content");
  const [activeFilter, setActiveFilter] = useState<StreamFilter>("all");

  const hasFastOption = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options?.allow_fast,
  );

  const allItems = streams.reduce<DisplayItem[]>(
    (acc, stream) => {
      if (stream.type === "thumbnails") return acc;

      return [...acc, { kind: "stream", data: stream, type: stream.type }];
    },
    fastAsset !== null
      ? [
          {
            kind: "fast-asset",
            data: fastAsset,
            type: "fast",
          },
        ]
      : [],
  );

  const filteredItems = allItems.filter((item) =>
    itemMatchesFilter(item, activeFilter),
  );

  const itemsByGroup = GROUP_ORDER.reduce<Record<GroupName, DisplayItem[]>>(
    (acc, group) => {
      acc[group] = filteredItems.filter((item) => groupForItem(item) === group);
      return acc;
    },
    { vodDelivery: [], offline: [], linearDelivery: [] },
  );

  function filterCount(filter: StreamFilter): number {
    return filter === "all"
      ? allItems.length
      : allItems.filter((item) => itemMatchesFilter(item, filter)).length;
  }

  const filterList = hasFastOption
    ? FILTERS
    : FILTERS.filter((filter) => filter !== "fast");

  return (
    <SidePanel
      withoutPadding
      className="w-[550px]! max-w-[100vw]!"
      isOpen={isOpen}
      onClose={onClose}
      title={t("encodingOperationsDrawerTitle")}
    >
      <div className="border-border bg-card sticky top-0 z-10 flex w-full gap-3 border-b p-3">
        {filterList.map((filter) => {
          const count = filterCount(filter);
          const isActive = activeFilter === filter;
          const filterButtonClassNames = classnames(
            "px-2.5 text-center font-medium uppercase",
          );
          const filterCountClassNames = classnames(
            isActive ? "text-primary-foreground" : "text-muted-foreground",
          );
          return (
            <Button
              key={filter}
              className={filterButtonClassNames}
              onClick={(): void => setActiveFilter(filter)}
              size="xs"
              type="button"
              variant={isActive ? "primary" : "secondary"}
              withGlow={isActive}
            >
              {t(`encodingDrawerFilters.${filter}`)}
              <span className={filterCountClassNames}>{count}</span>
            </Button>
          );
        })}
      </div>

      <div className="bg-muted flex flex-1 flex-col gap-8 p-5">
        {GROUP_ORDER.map((group) => {
          const groupItems = itemsByGroup[group];
          if (groupItems.length === 0) return null;
          return (
            <div key={group}>
              <div className="mb-3 flex items-center gap-3">
                <p className="text-muted-foreground text-[0.78125rem] font-medium tracking-wide uppercase">
                  {t(group)}
                </p>
                <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-[0.78125rem]">
                  {groupItems.length}
                </span>
              </div>
              <div className="flex flex-col gap-3">
                {groupItems.map((item) =>
                  item.kind === "stream" ? (
                    <CardOttAsset
                      key={item.data.uuid}
                      onDelete={(): void => onDeleteStream(item.data.uuid)}
                      onUpdateStream={onUpdateStream}
                      stream={item.data}
                    />
                  ) : (
                    <CardFastAsset
                      key={item.data.uuid}
                      fastAsset={item.data}
                      onDelete={onDeleteFastAsset}
                    />
                  ),
                )}
              </div>
            </div>
          );
        })}

        {filteredItems.length === 0 && (
          <p className="text-muted-foreground text-base">{t("noStreamsYet")}</p>
        )}
      </div>
    </SidePanel>
  );
}
